import Section, {Section2} from "@/components/Section";
import {Page} from "@/components/pages";
import Loading from "@/components/breadcrumbs/Loading";
import {PersonalDataList} from "@/components/extension/delete/PersonalDataList";
import {FormActionButtons, FormContainer} from "@dvrt/graphql-widgets";
import {List, ListItem, Stack, Grid} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import {
    DeleteExtensionMutation,
    DeleteExtensionMutationVariables,
    useDeleteExtensionMutation,
    useExtensionRelatedDataQuery,
    useGetExtensionsQuery,
} from "./queries.generated";
import ExtensionRelatedData from "./ExtensionRelatedData";
import {useEffect, useMemo, useState} from "react";
import {useForm} from "@/utils/Form";
import {useExtensionNumberIdQuery} from "../limit-settings/queries.generated";
import {useNavigate} from "react-router";
import {ReferenceRetargetsUser} from "@/api";
import {initialRelatedDataUser, processUserRelatedDataQueryResult} from "@/utils/relatedDataTools";
import {msg_delete} from "@/utils/translations";

const DeleteExtensionPage = ({domainId, extensionId}: {domainId: number; extensionId: number}) => {
    const initialState: DeleteExtensionMutationVariables = useMemo(
        () => ({
            userId: extensionId,
            deleteChatHistory: true,
            deletePersonalAddressbook: true,
            deleteVoicemailHistory: true,
            deletePresenceData: true,
            relatedData: {},
        }),
        [extensionId],
    );

    const [resolved, setResolved] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const navigate = useNavigate();

    // loads the related data belonging to this extension
    const {data: relatedData, loading} = useExtensionRelatedDataQuery({
        variables: {extensionId},
    });

    // loads the extensions belonging to this domain
    const extensionsData = useGetExtensionsQuery({
        variables: {domainId},
    });

    const {data: numberIdData, loading: numberIdLoading} = useExtensionNumberIdQuery({
        variables: {userId: extensionId},
        fetchPolicy: "no-cache",
    });

    // const form = new DeleteExtensionForm(domainId, extensionId);
    const form = useForm<DeleteExtensionMutation, DeleteExtensionMutationVariables>(
        useDeleteExtensionMutation,
        "deleteUser",
        initialState,
    );

    useEffect(() => {
        if (relatedData?.relatedData)
            form.newInitialData({...initialState, relatedData: initialRelatedDataUser(relatedData?.relatedData)});
    }, [relatedData?.relatedData, form, initialState]);

    // references
    const references = useMemo(() => {
        const extensionNumberId = numberIdData?.users.items[0].numberId;

        return processUserRelatedDataQueryResult(relatedData?.relatedData, extensionNumberId);
    }, [relatedData, numberIdData]);

    const resolutionCallback = (resolution: ReferenceRetargetsUser) => form.updateValues({relatedData: resolution});

    const deleteExtension = async () => {
        setDeleting(true);
        const result = await form.submit();
        setDeleting(false);
        if (result) {
            form.reset();
            navigate(`/domains/${domainId}/users`);
        }
    };

    if (loading || extensionsData.loading || numberIdLoading) return <Loading />;

    return (
        <Page title={defineMessage({id: "extension.delete.page-title", defaultMessage: "Delete user"})}>
            <Stack spacing={2}>
                <Section
                    label={defineMessage({
                        id: "extension.delete.membership-title",
                        defaultMessage: "The user is the member of the these groups",
                    })}
                    hidden={!relatedData.relatedData.groupMemberships.length}
                >
                    <List>
                        {relatedData.relatedData.groupMemberships.map((group, index) => (
                            <ListItem key={index}>
                                <FormattedMessage
                                    id="extension.delete.references.group-membership"
                                    defaultMessage="{name} ({number})"
                                    values={{
                                        name: group.name,
                                        number: group.number.shortNumber,
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Section>
                <Section2
                    label={defineMessage({
                        id: "extension.delete.related-title",
                        defaultMessage: "You need to resolve these references before deleting the user",
                    })}
                >
                    <Grid size={12}>
                        <ExtensionRelatedData
                            domainId={domainId}
                            resolvedCallback={setResolved}
                            resolutionCallback={resolutionCallback}
                            data={form.values.relatedData}
                            references={references}
                            errorHelper={form.errorHelper}
                        />
                    </Grid>
                </Section2>
                <Section
                    label={defineMessage({
                        id: "extension.delete.selections-title",
                        defaultMessage: "Please select what additional data should be deleted",
                    })}
                >
                    <FormContainer>
                        <PersonalDataList form={form} />
                    </FormContainer>
                </Section>
                <div>
                    <FormActionButtons
                        mainButtonText={msg_delete}
                        mainAction={deleteExtension}
                        mainButtonColor="secondary"
                        secondaryAction={() => {
                            form.reset();
                        }}
                        mainButtonEnabled={resolved && !deleting}
                        secondaryButtonEnabled={form.hasChanged && !deleting}
                        pending={deleting}
                    />
                </div>
            </Stack>
        </Page>
    );
};

export default DeleteExtensionPage;
